import { isPlatformAdminHost, tenantSlugFromHost } from '@self-restaurant/shared'
const TOKEN_KEY = 'sr_access_token'
const TENANT_KEY = 'sr_tenant_slug'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
  notifyToken()
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  notifyToken()
}

// ─── Sincronização do token entre abas (hotfix #288) ─────────────────────────
// localStorage é compartilhado; o evento `storage` avisa as OUTRAS abas quando
// uma delas renova (setToken) ou encerra (clearToken) a sessão.
const tokenListeners = new Set<() => void>()
function notifyToken(): void {
  for (const fn of tokenListeners) fn()
}
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (e) => {
    if (e.key === TOKEN_KEY || e.key === null) notifyToken()
  })
}
/** Assina mudanças do token (nesta aba ou em outra). Retorna o unsubscribe. */
export function onTokenChange(fn: () => void): () => void {
  tokenListeners.add(fn)
  return () => tokenListeners.delete(fn)
}

/**
 * Slug do tenant vindo da URL (path /r/:slug, subdomínio ou ?tenant=), sem
 * olhar o localStorage. null = a página não diz de que restaurante é.
 */
export function getTenantSlugFromUrl(): string | null {
  // 1. URL path /r/:slug/
  const pathMatch = window.location.pathname.match(/^\/r\/([^/]+)/)
  if (pathMatch?.[1]) return pathMatch[1]!

  // 2. Hostname: <slug>.admin.<domínio> | admin.<domínio>
  //    (pratzu.com, asknamba.com e self-restaurant.app — lista única no shared)
  const host = window.location.hostname
  const hostSlug = tenantSlugFromHost(host, 'admin')
  if (hostSlug) return hostSlug
  if (isPlatformAdminHost(host)) return 'platform'

  // 3. Query param ?tenant=
  const params = new URLSearchParams(window.location.search)
  const queryTenant = params.get('tenant')
  if (queryTenant) {
    setStoredTenantSlug(queryTenant)
    return queryTenant
  }
  return null
}

export function getTenantSlug(): string {
  // 4. localStorage fallback (validado contra a sessão em lib/session.ts, #319)
  return getTenantSlugFromUrl() ?? getStoredTenantSlug()
}

/** True se o X-Tenant-Slug desta página sairia do localStorage (#319). */
export function tenantSlugIsFromStorage(): boolean {
  return getTenantSlugFromUrl() === null && readStoredTenantSlugOrNull() !== null
}

/** Slug guardado, ou null se não houver (#319). */
export function readStoredTenantSlugOrNull(): string | null {
  try {
    return localStorage.getItem(TENANT_KEY)
  } catch {
    return null
  }
}

// Lê apenas do localStorage, ignora URL
export function getStoredTenantSlug(): string {
  return readStoredTenantSlugOrNull() ?? 'platform'
}

/**
 * Slug do restaurante da sessão, gravado no login (seletor), no handoff do
 * /contratar e ao confirmar a sessão em /r/:slug (#319).
 */
export function setStoredTenantSlug(slug: string): void {
  try {
    localStorage.setItem(TENANT_KEY, slug)
  } catch {
    // storage indisponível — o painel segue pelo path da URL
  }
}

/** Descarta o slug guardado: logout, tenant apagado ou de outra sessão (#319). */
export function clearStoredTenantSlug(): void {
  try {
    localStorage.removeItem(TENANT_KEY)
  } catch {
    // storage indisponível — nada guardado
  }
}

export function isAuthenticated(): boolean {
  const token = getToken()
  if (!token) return false
  try {
    // Verificação local simples: decodificar payload e checar exp
    const payload = JSON.parse(atob(token.split('.')[1] ?? ''))
    return (payload.exp as number) > Math.floor(Date.now() / 1000)
  } catch {
    return false
  }
}

export function getTokenPayload(): {
  sub: string
  role: string
  roles?: string[]
  tenant_id: string
  exp: number
  /** Sessão da plataforma (#430) */
  impersonating?: { tenant_id: string; restaurant_owner_id?: string | null }
} | null {
  const token = getToken()
  if (!token) return null
  try {
    return JSON.parse(atob(token.split('.')[1] ?? ''))
  } catch {
    return null
  }
}

/**
 * Roles efetivas do usuário: união de roles[] (multi-role) com role (fallback
 * para tokens antigos). Toda checagem de role na UI deve usar isto.
 */
export function effectiveRoles(
  payload: { role?: string | null; roles?: readonly string[] | null } | null | undefined,
): string[] {
  if (!payload) return []
  const set = new Set<string>()
  if (Array.isArray(payload.roles))
    for (const r of payload.roles) if (typeof r === 'string' && r) set.add(r)
  if (typeof payload.role === 'string' && payload.role) set.add(payload.role)
  return [...set]
}

/** True se o usuário tem pelo menos uma das roles permitidas. */
export function hasAnyRole(
  payload: { role?: string | null; roles?: readonly string[] | null } | null | undefined,
  allowed: readonly string[],
): boolean {
  return effectiveRoles(payload).some((r) => allowed.includes(r))
}

/** Roles efetivas do token atual. */
export function getUserRoles(): string[] {
  return effectiveRoles(getTokenPayload())
}

/** Filtra itens de menu pelas roles efetivas (sem roles = sem filtro). */
export function visibleSubItems<T extends { roles: readonly string[] }>(
  items: readonly T[],
  userRoles: readonly string[],
): T[] {
  if (userRoles.length === 0) return [...items]
  return items.filter(({ roles }) => roles.some((r) => userRoles.includes(r)))
}
